/* Copyright (c) 2020-present CloudIO Inc */

import { removeIn, setIn } from '@cloudio-saas/mutx';
import {
  useStateX,
  useStateXAtomSetter,
  useStateXSelector,
} from '@cloudio-saas/statex';
import {
  Box,
  Button,
  IconButton,
  Input,
  InputAdornment,
  PaperProps,
  TextField,
  Theme,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { makeStyles } from '@mui/styles';
import { ExpressionValue, OptionsProp } from 'componentregistry';
import { parseOptionValues } from 'components/Select/SelectUtils';
import { helpTextSelector } from 'designer/state';
import { Reorder, useDragControls } from 'framer-motion';
import useLocalState from 'hooks/useLocalState';
import useWindowSize from 'hooks/useWindowSize';
import Icon from 'icons/Icon';
import { DraggablePaperComponent } from 'platform/ExamineItemProps';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'theme/Theme';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';
import { expressionPropsAtom } from 'expression/builder/state';
import assert from 'utils/assert';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
  input: {
    '& input, textarea': {
      fontSize: '0.8rem',
    },
  },
  tip: {
    marginLeft: 8,
    marginBottom: 8,
    cursor: 'default',
  },
  s: {
    backgroundColor: theme.palette.background.paper,
    position: 'absolute',
    top: 4,
    bottom: 4,
    borderRadius: 4,
    boxShadow: 'rgb(0 0 0 / 25%) 0px 1px 6px 0px',
    transition: 'left 0.2s cubic-bezier(0.2,0,0,1)',
  },
  inner: {
    marginBottom: 8,
    height: 32,
    display: 'flex',
    width: 80,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
    justifyContent: 'space-evenly',
  },
}));

let counter = 0;

function genId() {
  return `a${++counter}`;
}

interface Option {
  id: string;
  value?: string;
  label?: string;
}

export function OptionsEntry({
  value,
  onClose,
  onChange,
}: {
  value?: string;
  onClose: () => void;
  onChange: (val: string) => void;
}) {
  const [options, setOptions] = useState<Option[]>(() =>
    (parseOptionValues(value ?? '') as unknown as Option[]).map((o) => ({
      ...o,
      id: genId(),
    })),
  );
  const [hideLabel, setHideLable] = useState(
    () => !options.find((o) => !!o.label && o.value !== o.label),
  );
  const theme = useTheme();
  const [, wh] = useWindowSize();
  const optionsWithNew = useMemo<Option[]>(() => {
    if (options.length === 0) {
      return [
        { id: genId() },
        { id: genId() },
        { id: genId() },
        { id: genId() },
        { id: genId() },
      ];
    }
    return options.length && options[options.length - 1].value
      ? [...options, { id: genId(), value: '', label: '' }]
      : options;
  }, [options]);
  const dragControls = useDragControls();

  function startDrag(event: React.PointerEvent<HTMLDivElement>) {
    dragControls.start(event);
  }

  const PaperComponent = useMemo(
    () => (props: PaperProps) =>
      <DraggablePaperComponent {...props} dragControls={dragControls} />,
    [dragControls],
  );

  return (
    <Dialog
      PaperComponent={PaperComponent}
      open
      onClose={(event, reason) => {
        if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
          onClose();
        }
      }}
    >
      <DialogTitle
        onPointerDown={startDrag}
        sx={{
          m: 0,
          p: 2,
          cursor: 'move',
        }}
      >
        Options
        <IconButton sx={{ float: 'right' }} onClick={onClose}>
          <span aria-hidden="false" style={{ display: 'none' }}>
            Close
          </span>
          <Icon iconName="times" />
        </IconButton>
        <IconButton
          sx={{ float: 'right' }}
          onClick={() => setHideLable(!hideLabel)}
          data-tip={`${hideLabel ? 'Show' : 'Hide'} Label`}
        >
          <Icon iconName={hideLabel ? 'eye' : 'eye-slash'} />
        </IconButton>
        <IconButton
          data-tip="Sort Options"
          sx={{ float: 'right' }}
          onClick={() => {
            setOptions(
              [...optionsWithNew].sort((a, b) => {
                if (!(a.value || b.value)) return 0;
                if (!a.value) return 1;
                if (!b.value) return -1;
                return (a.label || a.value).localeCompare(b.label || b.value);
              }),
            );
          }}
        >
          <Icon iconName="sort-alpha-up" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ maxHeight: wh - 64 - 40 - 54 }}>
        <Reorder.Group
          as="div"
          axis="y"
          values={optionsWithNew}
          onReorder={setOptions}
        >
          {optionsWithNew.map((item, index) => (
            <Reorder.Item
              draggable={index !== optionsWithNew.length - 1}
              as="div"
              key={item.id}
              value={item}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  '&:hover': { bgcolor: theme.palette.action.hover },
                  '&:hover .times': { visibility: 'visible' },
                  padding: '0 16px',
                  borderRadius: '16px',
                }}
              >
                <Icon
                  sx={{
                    cursor:
                      index !== optionsWithNew.length - 1 ? 'grab' : 'default',
                  }}
                  iconName={
                    index !== optionsWithNew.length - 1 ? 'bars' : 'plus'
                  }
                />
                <Input
                  value={item.value ?? ''}
                  onChange={(e) =>
                    setOptions(
                      setIn(optionsWithNew, [index, 'value'], e.target.value),
                    )
                  }
                  placeholder="Value"
                  disableUnderline
                  autoComplete="off"
                />
                {!hideLabel ? (
                  <Input
                    value={item.label || item.value}
                    onChange={(e) =>
                      setOptions(
                        setIn(optionsWithNew, [index, 'label'], e.target.value),
                      )
                    }
                    placeholder="Label"
                    disableUnderline
                    autoComplete="off"
                  />
                ) : null}
                {index === optionsWithNew.length - 1 ? null : (
                  <IconButton
                    className="times"
                    sx={{ visibility: 'hidden' }}
                    onClick={() =>
                      setOptions(removeIn(optionsWithNew, [index]))
                    }
                  >
                    <span aria-hidden="false" style={{ display: 'none' }}>
                      Remove Option
                    </span>
                    <Icon iconName="times" />
                  </IconButton>
                )}
              </Box>
            </Reorder.Item>
          ))}
        </Reorder.Group>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            onChange(
              options
                .filter((o) => !!o.value)
                .map((o) =>
                  o.label && o.label !== o.value
                    ? `${o.value}:${o.label}`
                    : o.value,
                )
                .join(','),
            );
            onClose();
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function OptionsProperty({
  appUid,
  activeItemId,
  def,
  mgr,
  pageId,
  property,
  value = getDefaultValues<ExpressionValue>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<OptionsProp, ExpressionValue>) {
  const classes = useStyles();
  const [undoable, setUndoable] = useState(true);
  const [open, setOpen] = useState(false);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const text2 = value;
  const text: ExpressionValue = useMemo(() => {
    const x: ExpressionValue =
      typeof text2 === 'string' ? { value: text2, type: 'text' } : text2;
    if (x.value === null) {
      return { type: 'text', value: '' };
    }
    return x;
  }, [text2]);
  const [localValue, setLocalValue] = useLocalState(text.value, (val) => {
    onChange(property, { type: text.type, value: val }, undoable);
    setUndoable(false);
  });
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });
  const setExpressionProps = useStateXAtomSetter(expressionPropsAtom, {
    params: { appUid, pageId, itemId: activeItemId },
  });
  const path = [
    'transient',
    '_expression_',
    appUid,
    pageId,
    activeItemId,
    property,
  ];
  const [expressionValue, setExpressionValue] = useStateX<
    ExpressionValue | undefined
  >(path, undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // reset the value in the trasient path
    setExpressionValue(undefined);
    return () => {
      setExpressionValue(undefined);
    };
  }, [setExpressionValue]);

  useEffect(() => {
    if (expressionValue) {
      // user have selected some item in the popup
      assert(
        typeof expressionValue.value === 'string',
        'expressionValue.value must be string',
      );
      setLocalValue(expressionValue.value);
      inputRef.current?.focus();
    }
  }, [expressionValue, setLocalValue]);

  const openExpressionBuilder = (val: string) => {
    assert(typeof val === 'string', 'val must be a string');
    setExpressionProps({
      path,
      returnType: 'string',
      open: true,
      type: 'expressionObject',
      value: { type: 'expression', value: val },
      context: {
        appUid,
        pageId,
        itemId: activeItemId,
        property,
        data: {},
      },
    });
  };

  return (
    <>
      <TextField
        disabled={isViewOnlyUser}
        inputRef={inputRef}
        autoComplete="off"
        className={classes.root}
        fullWidth
        label={def.label}
        variant="standard"
        required={required}
        value={localValue ?? ''}
        placeholder={def.placeholder}
        data-testid={property}
        onFocus={(e) => {
          setUndoable(true);
          e.target.select();
        }}
        onBlur={(e) => {
          setUndoable(true);
        }}
        classes={{ root: classes.input }}
        onChange={(e) => {
          setLocalValue(e.target.value);
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <div className={classes.inner}>
                <Box
                  sx={{
                    width: 'calc(50% - 8px)',
                    left: text.type === 'text' ? 5 : '53%',
                  }}
                  className={classes.s}
                />
                <IconButton
                  disabled={isViewOnlyUser}
                  onClick={() => {
                    let val = localValue;
                    if (text.type !== 'text') {
                      if (val && val.charAt(0) === '`') {
                        val = val.substring(1, val.length - 1);
                        setLocalValue(val);
                      }
                      onChange(property, { value: val, type: 'text' });
                      setExpressionValue(undefined);
                    }
                    setOpen(true);
                  }}
                  data-tip="Edit Options"
                >
                  <Icon disabled={isViewOnlyUser} iconName="list" />
                </IconButton>
                <IconButton
                  disabled={isViewOnlyUser}
                  onClick={() => {
                    let val = localValue;
                    if (text.type !== 'expression') {
                      if (val && val.charAt(0) !== '`') {
                        val = `\`${val}\``;
                        setLocalValue(val);
                      }
                      //val = '';
                      setLocalValue(val);
                      onChange(property, {
                        value: val,
                        type: 'expression',
                      });
                    }
                    setExpressionValue(undefined);
                    openExpressionBuilder(val as string);
                  }}
                  data-tip={
                    text.type === 'expression'
                      ? 'Open Expression Builder'
                      : 'Expression'
                  }
                >
                  <Icon disabled={isViewOnlyUser} iconName="sigma" />
                </IconButton>
              </div>
              {helpText ? (
                <Icon
                  disabled={isViewOnlyUser}
                  className={classes.tip}
                  iconName="comment-exclamation"
                  data-tip={helpText}
                  data-tip-delay={0}
                  data-tip-at="top"
                />
              ) : null}
            </InputAdornment>
          ),
        }}
        // eslint-disable-next-line react/jsx-no-duplicate-props
        inputProps={{
          'data-testid': `${property}-input`,
        }}
      />
      {open ? (
        <OptionsEntry
          onClose={() => setOpen(false)}
          onChange={setLocalValue}
          value={localValue}
        />
      ) : null}
    </>
  );
}

export default memo(OptionsProperty);
